import { useState } from 'react'
import { Check, Pencil, X } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useInitFromServer } from '@/hooks/useInitFromServer'
import { useUpdateEvalCaseMutation } from '../../hooks/useUpdateEvalCaseMutation'

export function EditableCaseName({
  caseId,
  name,
}: {
  caseId: string
  name: string
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(name)
  const updateCase = useUpdateEvalCaseMutation(caseId)

  // Re-sync from server when not mid-edit (e.g. after a save refetch).
  useInitFromServer(name, (next) => {
    if (!isEditing) setDraft(next)
  })

  const commit = () => {
    const trimmed = draft.trim()
    if (!trimmed || trimmed === name) {
      setDraft(name)
      setIsEditing(false)
      return
    }
    updateCase.mutate(
      { name: trimmed },
      {
        onSuccess: () => {
          toast.success('Name saved')
          setIsEditing(false)
        },
        onError: (error: Error) => {
          toast.error(error.message || 'Failed to save name')
        },
      }
    )
  }

  const cancel = () => {
    setDraft(name)
    setIsEditing(false)
  }

  if (!isEditing) {
    return (
      <div className="group flex min-w-0 items-center gap-1.5">
        <h1 className="min-w-0 truncate text-[21px] font-extrabold text-primary-high-contrast">
          {name}
        </h1>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Edit name"
          className="shrink-0 text-neutral-low-contrast opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
          onClick={() => {
            setDraft(name)
            setIsEditing(true)
          }}>
          <Pencil className="size-4" />
        </Button>
      </div>
    )
  }

  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <Input
        autoFocus
        value={draft}
        aria-label="Test case name"
        disabled={updateCase.isPending}
        className="h-9 max-w-md text-[17px] font-bold"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
          if (e.key === 'Escape') cancel()
        }}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Save name"
        disabled={updateCase.isPending}
        onClick={commit}>
        <Check className="size-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Cancel name edit"
        disabled={updateCase.isPending}
        onClick={cancel}>
        <X className="size-4" />
      </Button>
    </div>
  )
}
